import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Share } from 'lucide-react';

const InstallAppButton = ({ className = '' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS (more comprehensive detection)
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const iOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    // Also check for iPad on iOS 13+ which reports as Mac
    const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    setIsIOS(iOS || iPadOS);

    // Listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      // Fallback - show instructions modal
      setShowIOSModal(true);
    }
  };

  // Don't show if already installed as standalone
  if (isStandalone) return null;

  return (
    <>
      <button
        onClick={handleInstallClick}
        className={`px-8 py-4 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2 ${className}`}
        data-testid="install-app-btn"
      >
        <Download size={20} />
        Install App
      </button>

      {/* iOS/Fallback Instructions Modal */}
      {showIOSModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center">
                  <Smartphone className="text-white" size={32} />
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-stone-800 text-center mb-2">
                Install XRU Alert
              </h3>
              <p className="text-stone-600 text-center text-sm mb-6">
                Add this app to your home screen for quick access to community alerts.
              </p>

              {isIOS ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-stone-100 rounded-lg">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Share className="text-white" size={16} />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm">Step 1</p>
                      <p className="text-stone-600 text-sm">Tap the <strong>Share</strong> button in Safari</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-stone-100 rounded-lg">
                    <div className="w-8 h-8 bg-stone-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">+</span>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm">Step 2</p>
                      <p className="text-stone-600 text-sm">Scroll down and tap <strong>"Add to Home Screen"</strong></p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-stone-100 rounded-lg">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm">Step 3</p>
                      <p className="text-stone-600 text-sm">Tap <strong>"Add"</strong> in the top right</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-stone-600 text-sm text-center">
                    Look for the install icon <Download size={16} className="inline" /> in your browser's address bar, or use your browser's menu to "Add to Home Screen" or "Install App".
                  </p>
                </div>
              )}
            </div>
            
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowIOSModal(false)}
                className="w-full py-3 bg-stone-800 hover:bg-stone-700 text-white font-semibold rounded-lg transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
